import { DragEvent, useCallback, useRef, useState } from "react";

interface UseFileDropOptions {
  // Called with the text contents of a dropped or selected file. The originating
  // File is passed too so callers can default a filename from file.name.
  onText: (text: string, file: File) => void;
  // Maximum file size in bytes. Files larger than this are rejected.
  maxBytes: number;
  // Called with an error message when a file is too large or unreadable,
  // and with null when a valid file is loaded.
  onError?: (message: string | null) => void;
}

// UTF-8 when the bytes are valid UTF-8, Windows-1252 otherwise, so a Latin-1
// file keeps its accents. file.text() would turn them into U+FFFD. The server
// (Edi::TextEncoding) converts byte by byte instead; files that mix the two
// are rare enough not to matter here.
function decodeText(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

// Shared "paste, drag a file onto the editor, or use Upload file" input
// affordance. Spread `dragProps` onto the element that should accept drops,
// render `fileInput` somewhere in the tree, and wire `openFilePicker` to an
// Upload button. `isDragging` is provided for drop-target styling.
export function useFileDrop({ onText, maxBytes, onError }: UseFileDropOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      onError?.(null);
      if (file.size > maxBytes) {
        onError?.(
          `Input exceeds maximum size of ${Math.round(maxBytes / 1024)}KB.`,
        );
        return;
      }
      let buffer: ArrayBuffer;
      try {
        buffer = await file.arrayBuffer();
      } catch {
        onError?.("Could not read that file. Please try another.");
        return;
      }
      onText(decodeText(buffer), file);
    },
    [maxBytes, onText, onError],
  );

  const dragProps = {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsDragging(false);
      loadFile(e.dataTransfer.files?.[0]);
    },
  };

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={(e) => loadFile(e.target.files?.[0] ?? undefined)}
    />
  );

  return { isDragging, dragProps, openFilePicker, fileInput };
}
